import { Button } from '@/components/Buttons';
import { BUTTON_LABELS, FORM_LABELS, MANAGER_LABELS, TABLE_LABELS, TAG_LABELS } from '@/constants';
import { formatDate } from '@/utils';
import { handleManagerSharedCallbackClick } from '@/features/managed-user-dashboard/utils/managed-user-shared-callbacks';
import { handleManagerSharedProductChangeCallback } from '@/features/managed-user-dashboard/utils/managed-user-shared-callbacks';
import { IManagedUserSession } from '@/types/manager';
import { ITableColumn, Table } from '@/components/Tables/Table';
import { ManagedUserTabTableProps as Props } from '@/features/managed-user-dashboard';
import { ManagerTableCellLabel as ManagerDashboardTableCellLabel } from '@/components/Tables/ManagerTablesComponents';
import { NativeSelect } from '@/components/Fields';
import { PassFailTag } from '@/components/Tags';
import { sortBy } from 'lodash';
import { TextSplitMap } from '@/components/Text';
import { useManagerActiveProduct } from '@/features/management';
import React, { useMemo } from 'react';

export const ManagedUserTabTableCertifications: React.FC<Props> = React.memo(({
  activeProduct,
  isLoading = false,
  managedUser,
  onProductChange,
  onViewCertificateClick,
  onViewScorecardClick,
  productOptions = [],
}) => {
  /**
   * Currently active product option.
   */
  const activeProductOption = useManagerActiveProduct({ productOptions, activeProduct });

  /**
   * Memoized placeholder text for the product select dropdown. If an active product option
   * is selected, the placeholder will prompt the user to change the product. If no product
   * is selected, it will prompt the user to select a product.
   */
  const productPlaceholder = useMemo(() => {
    return activeProductOption ? FORM_LABELS.PRODUCT_CHANGE : FORM_LABELS.PRODUCT_SELECT;
  }, [activeProductOption]);

  /**
   * Memoized table data for certifications. Filters the managedUser's certificates based
   * on the active product selection (if any), and sorts them by attempt number in descending
   * order so the most recent attempt appears first. Returns an empty array if managedUser or
   * certificates are undefined.
   */
  const tableData: IManagedUserSession[] = useMemo(() => {
    if (!managedUser) return []; // Return empty array if managedUser is undefined
    if (!managedUser.certificates) return []; // Return empty array if certificates is undefined

    // Filter certificates based on active product selection, or all
    const filtered = managedUser.certificates.filter((cert) => {
      return !activeProductOption
        ? true
        : cert.certificationId.toString() === activeProductOption.value;
    });

    // Sort by attempt number in descending order so the most recent attempt appears first
    const sorted = sortBy(filtered, (cert) => cert.attemptNumber).reverse();

    // Return sorted certificates
    return sorted;
  }, [managedUser, activeProductOption]);

  /**
   * Callbacks object that maps callback names to their respective handler functions. This is used to
   * pass the appropriate callback functions to child components without having to define them inline,
   * which can help with performance and readability.
   */
  const callbacks = {
    handleScorecardClick: handleManagerSharedCallbackClick,
    handleProductChange: handleManagerSharedProductChangeCallback,
  };

  /**
   * Table column definitions for the certifications table.
   * Defines columns for Product, Status, Date Certified, and Actions.
   */
  const tableColumns: ITableColumn[] = [
    {
      key: 'certificationTitleShort',
      title: TABLE_LABELS.PRODUCT,
      width: 145,
      render: (product: string) => <ManagerDashboardTableCellLabel label={product} />,
    },
    {
      key: 'status',
      title: TABLE_LABELS.STATUS,
      width: 240,
      render: (_, record: IManagedUserSession) => {
        let failText = TAG_LABELS.FAIL as string;
        if (record.attemptNumber === 1) failText = TAG_LABELS.FAIL_1ST_ATTEMPT;
        else if (record.attemptNumber === 2) failText = TAG_LABELS.FAIL_2ND_ATTEMPT;

        return (
          <div className="flex items-center justify-start gap-2">
            <PassFailTag
              className="antialiased"
              simulationType="certification"
              textForFail={failText}
              variant={
                record.attemptCount === 0 ? 'not attempted' : record.passed ? 'pass' : 'fail'
              }
            />
          </div>
        );
      },
    },
    {
      key: 'submittedAt',
      title: TABLE_LABELS.DATE_CERTIFIED,
      width: 255,
      render: (date: string, record: IManagedUserSession) => {
        const formattedDate = date ? formatDate(date, 'shortDate') : TABLE_LABELS.NA;

        if (record.attemptCount === 0 || !record.passed) {
          return <ManagerDashboardTableCellLabel label={TABLE_LABELS.NA} />;
        }

        return <ManagerDashboardTableCellLabel label={formattedDate} />;
      },
    },
    {
      key: 'actions',
      title: TABLE_LABELS.ACTIONS,
      headerClassName: 'opacity-0',
      render: (_, record) => {
        return (
          <div className="flex items-center justify-start gap-10">
            <Button
              label={BUTTON_LABELS.VIEW_SCORECARD}
              size="small"
              variant="link"
              onClick={() =>
                callbacks.handleScorecardClick({ record, callback: onViewScorecardClick })
              }
            />
            {record.passed && (
              <Button
                label={BUTTON_LABELS.VIEW_CERTIFICATE}
                size="small"
                variant="link"
                onClick={() =>
                  callbacks.handleScorecardClick({ record, callback: onViewCertificateClick })
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div>
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-2xl font-normal text-neutral-black">
            {MANAGER_LABELS.CERTIFICATION_SCORECARDS_FINAL}
          </h2>
          <NativeSelect
            id="product-select-certifications"
            isLoading={isLoading}
            name="Product Select"
            onChangeCallback={(value) => callbacks.handleProductChange(value, onProductChange)}
            options={productOptions}
            placeholder={productPlaceholder}
            value={activeProductOption?.value || 'all'}
          />
        </div>

        <div className="mb-6 text-sm leading-relaxed text-neutral-black">
          <TextSplitMap
            text={MANAGER_LABELS.MANAGED_USER_DESCRIPTION_CERTIFICATIONS}
            className="mb-1"
          />
        </div>
      </div>

      <Table columns={tableColumns} data={tableData} loading={isLoading} />
    </div>
  );
});

ManagedUserTabTableCertifications.displayName = 'ManagedUserTabTableCertifications';

export default ManagedUserTabTableCertifications;
